import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, User, MapPin, UtensilsCrossed, Truck, Sparkles, Shield } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PhoneAuthService } from '../../firebase/phoneAuth';

interface CustomerData {
  phone: string;
  name: string;
  orderType: 'dine-in' | 'delivery' | null;
  address?: string;
  tableNumber?: string;
  otp?: string;
}

const CustomerLogin: React.FC = () => {
  const [step, setStep] = useState<'phone' | 'otp' | 'name' | 'orderType' | 'details'>('phone');
  const [customerData, setCustomerData] = useState<CustomerData>({
    phone: '',
    name: '',
    orderType: null,
    otp: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [otpFocused, setOtpFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [devOTP, setDevOTP] = useState<string>('');
  
  const { setCurrentScreen } = useApp();
  const { refreshCustomerSession } = useAuth();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      PhoneAuthService.cleanup();
    };
  }, []);

  // Resend timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Update development OTP every minute (display only, don't auto-fill)
  useEffect(() => {
    if (devOTP && step === 'otp') {
      const updateDevOTP = () => {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const newOTP = hours + minutes;
        
        if (newOTP !== devOTP) {
          setDevOTP(newOTP);
          // Don't auto-fill the OTP field
        }
      };

      const interval = setInterval(updateDevOTP, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [devOTP, step]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (customerData.phone.length >= 10) {
      setIsLoading(true);
      setError('');
      
      try {
        // Format phone number (add +1 if not present)
        let formattedPhone = customerData.phone.replace(/\D/g, '');
        if (!formattedPhone.startsWith('1') && formattedPhone.length === 10) {
          formattedPhone = '1' + formattedPhone;
        }
        formattedPhone = '+' + formattedPhone;
        
        console.log('📱 Formatted phone:', formattedPhone);
        
        const result = await PhoneAuthService.sendOTP(formattedPhone);
        
        if (result.success) {
          console.log('✅ OTP sent, updating phone to:', formattedPhone);
          
          // Update customer data with formatted phone (don't auto-fill OTP)
          setCustomerData(prev => {
            const updated = { 
              ...prev, 
              phone: formattedPhone
            };
            console.log('📝 Updated customer data:', updated);
            return updated;
          });
          
          setOtpSent(true);
          setResendTimer(60); // 60 seconds cooldown
          
          // Store development OTP if provided
          if (result.devOTP) {
            setDevOTP(result.devOTP);
          }
          
          setStep('otp');
        } else {
          setError(result.error || 'Failed to send OTP');
        }
      } catch (error) {
        console.error('❌ Phone submit error:', error);
        setError('Failed to send OTP. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setError('Please enter a valid phone number');
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (customerData.otp && customerData.otp.length === 4) {
      setIsLoading(true);
      setError('');
      
      try {
        // Check if we're in development mode
        const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
        
        if (isDevelopment) {
          // In development, verify against time-based OTP (4 digits)
          const now = new Date();
          const hours = now.getHours().toString().padStart(2, '0');
          const minutes = now.getMinutes().toString().padStart(2, '0');
          const currentOTP = hours + minutes;
          
          // Also check previous minute for edge cases
          const prevMinute = new Date(now.getTime() - 60000);
          const prevHours = prevMinute.getHours().toString().padStart(2, '0');
          const prevMins = prevMinute.getMinutes().toString().padStart(2, '0');
          const previousOTP = prevHours + prevMins;
          
          if (customerData.otp === currentOTP || customerData.otp === previousOTP) {
            console.log('✅ Development OTP verified successfully');
            setStep('name');
          } else {
            console.log('❌ Invalid development OTP. Expected:', currentOTP, 'or', previousOTP, 'Got:', customerData.otp);
            setError(`Invalid OTP. Use current time: ${currentOTP}`);
          }
        } else {
          // In production, use Firebase OTP verification
          const result = await PhoneAuthService.verifyOTP(customerData.otp, {
            name: 'temp', // We'll get the real name in the next step
            phone: customerData.phone,
            orderType: 'dine-in' // Temporary, will be selected later
          });
          
          if (result.success) {
            console.log('✅ Production OTP verified successfully');
            setStep('name');
          } else {
            setError(result.error || 'Invalid OTP. Please try again.');
          }
        }
      } catch (error) {
        console.error('❌ OTP verification error:', error);
        setError('Invalid OTP. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setError('Please enter the 4-digit OTP');
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setIsLoading(true);
    try {
      const result = await PhoneAuthService.sendOTP(customerData.phone);
      if (result.success) {
        setResendTimer(60);
        setError('');
        
        // Update development OTP if provided (display only)
        if (result.devOTP) {
          setDevOTP(result.devOTP);
        }
      } else {
        setError(result.error || 'Failed to resend OTP');
      }
    } catch (error) {
      setError('Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setStep('orderType');
  };

  const handleOrderTypeSelect = (orderType: 'dine-in' | 'delivery') => {
    setCustomerData(prev => ({ ...prev, orderType }));
    
    if (orderType === 'delivery') {
      setStep('details');
    } else {
      // For dine-in, proceed directly to complete the process
      handleCompleteLogin(orderType);
    }
  };

  const handleDeliverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerData.address || customerData.address.trim().length < 10) {
      setError('Please enter a complete delivery address');
      return;
    }
    
    handleCompleteLogin('delivery');
  };

  const handleCompleteLogin = async (orderType: 'dine-in' | 'delivery') => {
    setIsLoading(true);
    setError('');
    
    try {
      // In production, verify OTP and create customer
      const result = await PhoneAuthService.verifyOTP(customerData.otp || '1234', {
        name: customerData.name,
        phone: customerData.phone,
        orderType: orderType,
        address: customerData.address
      });
      
      if (result.success && result.user) {
        // Create customer session
        const customerSession = {
          id: result.user.id,
          email: result.user.email,
          name: result.user.name,
          role: 'customer' as const,
          phone: result.user.phone,
          orderType: orderType,
          address: result.user.address,
          loginComplete: true
        };
        
        // Clear any existing admin session and set customer session
        localStorage.removeItem('bookfood_user');
        localStorage.setItem('bookfood_customer_session', JSON.stringify(customerSession));
        
        // Refresh auth context to load customer session
        refreshCustomerSession();
        
        // Navigate based on order type
        if (orderType === 'delivery') {
          setCurrentScreen('customer-home');
        } else {
          setCurrentScreen('customer-table-selection');
        }
      } else {
        setError(result.error || 'Failed to create account');
      }
    } catch (error) {
      setError('Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!customerData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!customerData.orderType) {
      setError('Please select order type');
      return;
    }
    
    if (customerData.orderType === 'delivery' && (!customerData.address || customerData.address.trim().length < 10)) {
      setError('Please enter a complete delivery address');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // In production, verify OTP and create customer
      const result = await PhoneAuthService.verifyOTP(customerData.otp || '1234', {
        name: customerData.name,
        phone: customerData.phone,
        orderType: customerData.orderType,
        address: customerData.address
      });
      
      if (result.success && result.user) {
        // Create customer session
        const customerSession = {
          id: result.user.id,
          email: result.user.email,
          name: result.user.name,
          role: 'customer' as const,
          phone: result.user.phone,
          orderType: customerData.orderType,
          address: result.user.address,
          loginComplete: true
        };
        
        // Clear any existing admin session and set customer session
        localStorage.removeItem('bookfood_user');
        localStorage.setItem('bookfood_customer_session', JSON.stringify(customerSession));
        
        // Refresh auth context to load customer session
        refreshCustomerSession();
        
        // Navigate based on order type
        if (customerData.orderType === 'delivery') {
          setCurrentScreen('customer-home');
        } else {
          setCurrentScreen('customer-table-selection');
        }
      } else {
        setError(result.error || 'Failed to create account');
      }
    } catch (error) {
      setError('Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  const renderPhoneStep = () => (
    <motion.form
      key="phone-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      onSubmit={handlePhoneSubmit}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5DC' }}>
          Welcome to Hotel Hub
        </h2>
        <p className="text-[#AEAEB2]">Enter your phone number to get started</p>
      </div>

      <div>
        <label className="block text-base font-semibold mb-3" style={{ color: '#F5F5DC' }}>
          Phone Number
        </label>
        <motion.div
          className="relative"
          animate={{ scale: phoneFocused ? 1.02 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Phone 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
            style={{ color: phoneFocused ? '#D4AF37' : '#AEAEB2' }}
            size={22} 
          />
          <input
            type="tel"
            value={customerData.phone}
            onChange={(e) => {
              console.log('📱 Phone input changed:', e.target.value);
              setCustomerData(prev => ({ ...prev, phone: e.target.value }));
            }}
            onFocus={() => setPhoneFocused(true)}
            onBlur={() => setPhoneFocused(false)}
            className="w-full pl-12 pr-4 py-4 rounded-xl text-white text-base transition-all duration-200"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: `2px solid ${phoneFocused ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
              outline: 'none'
            }}
            placeholder="+1 (555) 123-4567"
            required
          />
        </motion.div>
      </div>

      <motion.button
        type="submit"
        disabled={isLoading}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block w-5 h-5 border-2 border-t-transparent rounded-full"
              style={{ borderColor: '#0A0A0B', borderTopColor: 'transparent' }}
            />
            Sending OTP...
          </span>
        ) : (
          'Send OTP'
        )}
      </motion.button>
    </motion.form>
  );

  const renderOtpStep = () => (
    <motion.form
      key="otp-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      onSubmit={handleOtpSubmit}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5DC' }}>
          Verify Your Phone
        </h2>
        <p className="text-[#AEAEB2]">
          Enter the 4-digit code sent to<br />
          <span className="font-semibold" style={{ color: '#D4AF37' }}>
            {customerData.phone || 'your phone number'}
          </span>
        </p>
        {!customerData.phone && (
          <p className="text-xs text-red-400 mt-2">
            ⚠️ Phone number missing - please go back and re-enter
          </p>
        )}

      </div>

      <div>
        <label className="block text-base font-semibold mb-3" style={{ color: '#F5F5DC' }}>
          4-Digit Verification Code
        </label>
        <motion.div
          className="relative"
          animate={{ scale: otpFocused ? 1.02 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Shield 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
            style={{ color: otpFocused ? '#D4AF37' : '#AEAEB2' }}
            size={22} 
          />
          <input
            type="text"
            value={customerData.otp || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCustomerData(prev => ({ ...prev, otp: value }));
            }}
            onFocus={() => setOtpFocused(true)}
            onBlur={() => setOtpFocused(false)}
            className="w-full pl-12 pr-4 py-4 rounded-xl text-white text-base transition-all duration-200 text-center tracking-widest"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: `2px solid ${otpFocused ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
              outline: 'none',
              fontSize: '18px',
              letterSpacing: '8px'
            }}
            placeholder="0000"
            maxLength={4}
            required
          />
        </motion.div>
      </div>

      <div className="text-center">
        <p className="text-[#AEAEB2] text-sm mb-2">
          Didn't receive the code?
        </p>
        <motion.button
          type="button"
          onClick={handleResendOtp}
          disabled={resendTimer > 0 || isLoading}
          whileHover={{ scale: resendTimer > 0 ? 1 : 1.05 }}
          whileTap={{ scale: resendTimer > 0 ? 1 : 0.95 }}
          className="text-sm font-semibold transition-colors duration-200 disabled:opacity-50"
          style={{ color: resendTimer > 0 ? '#AEAEB2' : '#D4AF37' }}
        >
          {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
        </motion.button>
      </div>

      <div className="flex gap-3">
        <motion.button
          type="button"
          onClick={() => {
            console.log('🔙 Going back to phone step, current phone:', customerData.phone);
            setStep('phone');
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 py-4 text-lg font-semibold rounded-xl transition-all duration-200"
          style={{
            background: 'rgba(28, 28, 30, 0.6)',
            border: '2px solid rgba(212, 175, 55, 0.3)',
            color: '#F5F5DC'
          }}
        >
          Back
        </motion.button>
        <motion.button
          type="submit"
          disabled={isLoading || !customerData.otp || customerData.otp.length !== 4}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 btn-primary py-4 text-lg font-semibold disabled:opacity-50"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block w-5 h-5 border-2 border-t-transparent rounded-full"
                style={{ borderColor: '#0A0A0B', borderTopColor: 'transparent' }}
              />
              Verifying...
            </span>
          ) : (
            'Verify'
          )}
        </motion.button>
      </div>
    </motion.form>
  );

  const renderDetailsStep = () => (
    <motion.form
      key="name-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      onSubmit={handleNameSubmit}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5DC' }}>
          Nice to meet you!
        </h2>
        <p className="text-[#AEAEB2]">What should we call you?</p>
      </div>

      <div>
        <label className="block text-base font-semibold mb-3" style={{ color: '#F5F5DC' }}>
          Your Name
        </label>
        <motion.div
          className="relative"
          animate={{ scale: nameFocused ? 1.02 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <User 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
            style={{ color: nameFocused ? '#D4AF37' : '#AEAEB2' }}
            size={22} 
          />
          <input
            type="text"
            value={customerData.name}
            onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
            className="w-full pl-12 pr-4 py-4 rounded-xl text-white text-base transition-all duration-200"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: `2px solid ${nameFocused ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
              outline: 'none'
            }}
            placeholder="Enter your name"
            required
          />
        </motion.div>
      </div>

      <div className="flex gap-3">
        <motion.button
          type="button"
          onClick={() => setStep('otp')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 py-4 text-lg font-semibold rounded-xl transition-all duration-200"
          style={{
            background: 'rgba(28, 28, 30, 0.6)',
            border: '2px solid rgba(212, 175, 55, 0.3)',
            color: '#F5F5DC'
          }}
        >
          Back
        </motion.button>
        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 btn-primary py-4 text-lg font-semibold"
        >
          Continue
        </motion.button>
      </div>
    </motion.form>
  );

  const renderOrderTypeStep = () => (
    <motion.div
      key="order-type-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5DC' }}>
          How would you like to order?
        </h2>
        <p className="text-[#AEAEB2]">Choose your preferred dining option</p>
      </div>

      <div className="space-y-4">
        <motion.button
          onClick={() => handleOrderTypeSelect('dine-in')}
          whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(212, 175, 55, 0.3)" }}
          whileTap={{ scale: 0.98 }}
          className="w-full p-6 rounded-xl transition-all duration-200 text-left"
          style={{
            background: 'rgba(28, 28, 30, 0.6)',
            border: '2px solid rgba(212, 175, 55, 0.3)'
          }}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)' }}>
              <UtensilsCrossed size={24} className="text-[#0A0A0B]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-1" style={{ color: '#F5F5DC' }}>
                Dine In
              </h3>
              <p className="text-[#AEAEB2]">Select a table and enjoy our restaurant</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          onClick={() => handleOrderTypeSelect('delivery')}
          whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(212, 175, 55, 0.3)" }}
          whileTap={{ scale: 0.98 }}
          className="w-full p-6 rounded-xl transition-all duration-200 text-left"
          style={{
            background: 'rgba(28, 28, 30, 0.6)',
            border: '2px solid rgba(212, 175, 55, 0.3)'
          }}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)' }}>
              <Truck size={24} className="text-[#0A0A0B]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-1" style={{ color: '#F5F5DC' }}>
                Delivery
              </h3>
              <p className="text-[#AEAEB2]">Get your order delivered to your location</p>
            </div>
          </div>
        </motion.button>
      </div>

      <motion.button
        onClick={() => setStep('name')}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full py-4 text-lg font-semibold rounded-xl transition-all duration-200"
        style={{
          background: 'rgba(28, 28, 30, 0.6)',
          border: '2px solid rgba(212, 175, 55, 0.3)',
          color: '#F5F5DC'
        }}
      >
        Back
      </motion.button>
    </motion.div>
  );

  const renderDeliveryDetailsStep = () => (
    <motion.form
      key="delivery-details-step"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      onSubmit={handleDeliverySubmit}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5DC' }}>
          Delivery Address
        </h2>
        <p className="text-[#AEAEB2]">Where should we deliver your order?</p>
      </div>

      <div>
        <label className="block text-base font-semibold mb-3" style={{ color: '#F5F5DC' }}>
          Full Address
        </label>
        <motion.div
          className="relative"
          animate={{ scale: addressFocused ? 1.02 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <MapPin 
            className="absolute left-4 top-4 transition-colors duration-200"
            style={{ color: addressFocused ? '#D4AF37' : '#AEAEB2' }}
            size={22} 
          />
          <textarea
            value={customerData.address || ''}
            onChange={(e) => setCustomerData(prev => ({ ...prev, address: e.target.value }))}
            onFocus={() => setAddressFocused(true)}
            onBlur={() => setAddressFocused(false)}
            className="w-full pl-12 pr-4 py-4 rounded-xl text-white text-base transition-all duration-200 resize-none"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: `2px solid ${addressFocused ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
              outline: 'none',
              minHeight: '120px'
            }}
            placeholder="Enter your complete delivery address including street, city, and postal code"
            required
          />
        </motion.div>
      </div>

      <div className="flex gap-3">
        <motion.button
          type="button"
          onClick={() => setStep('orderType')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 py-4 text-lg font-semibold rounded-xl transition-all duration-200"
          style={{
            background: 'rgba(28, 28, 30, 0.6)',
            border: '2px solid rgba(212, 175, 55, 0.3)',
            color: '#F5F5DC'
          }}
        >
          Back
        </motion.button>
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 btn-primary py-4 text-lg font-semibold disabled:opacity-50"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block w-5 h-5 border-2 border-t-transparent rounded-full"
                style={{ borderColor: '#0A0A0B', borderTopColor: 'transparent' }}
              />
              Starting...
            </span>
          ) : (
            'Start Ordering'
          )}
        </motion.button>
      </div>
    </motion.form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0B] via-[#1C1C1E] to-[#0A0A0B] flex flex-col relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 rounded-full mix-blend-screen filter blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Header */}
      <div className="pt-16 pb-8 px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center shadow-2xl relative border-2"
            style={{ 
              background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
              borderColor: 'rgba(212, 175, 55, 0.5)',
              boxShadow: '0 0 40px rgba(212, 175, 55, 0.3)'
            }}
          >
            <UtensilsCrossed size={32} className="text-[#0A0A0B]" strokeWidth={2.5} />
          </motion.div>
        </motion.div>
      </div>

      {/* Form Container */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-1 px-6 max-w-md mx-auto w-full relative z-10"
      >
        <div
          className="backdrop-blur-xl p-8 rounded-2xl shadow-2xl"
          style={{
            background: 'rgba(44, 44, 46, 0.7)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}
        >
          <AnimatePresence mode="wait">
            {step === 'phone' && renderPhoneStep()}
            {step === 'otp' && renderOtpStep()}
            {step === 'name' && renderDetailsStep()}
            {step === 'orderType' && renderOrderTypeStep()}
            {step === 'details' && renderDeliveryDetailsStep()}
          </AnimatePresence>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-4 text-red-400 text-sm text-center bg-red-900 bg-opacity-30 p-4 rounded-xl border border-red-500 border-opacity-50"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Admin Login Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 text-center"
        >
          <motion.button
            onClick={() => {
              // Clear customer session when switching to admin login
              localStorage.removeItem('bookfood_customer_session');
              setCurrentScreen('admin-login');
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="text-sm transition-colors duration-200"
            style={{ color: '#AEAEB2' }}
          >
            Staff Login →
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CustomerLogin;